/**
 * Shared utility for parsing the response from GET client/quote-details/{id}.
 *
 * Both WorkshopDetailsPage and AdminDetailsPage use this mapper so the
 * JSON.parse of estimativa.itens happens exactly once and the data shape
 * matches what the shared section components (ClienteSection, EstimativaSection,
 * OrcamentoSection) already expect.
 */

function safeParseItens(itensRaw) {
  if (!itensRaw) return null;

  if (typeof itensRaw === 'object' && itensRaw !== null) {
    return itensRaw;
  }

  if (typeof itensRaw === 'string') {
    try {
      return JSON.parse(itensRaw);
    } catch (e) {
      console.error('Erro ao parsear estimativa.itens:', e);
      return null;
    }
  }

  return null;
}

/**
 * Format client data from the API response.
 */
function formatClientData(client) {
  if (!client) return null;

  return {
    empresa: client.empresa || '-',
    nome: client.nome || '-',
    telefone: client.telefone || '-',
    cnpj: client.cnpj || '-',
    dt_criacao: client.dtCriacao || client.dt_criacao || null,
    email: client.email || '-',
  };
}

/**
 * Format estimativa data from the API response.
 * The itens field is parsed from its JSON string into an object containing
 * servicos, localizacao, equipamento_info, and equipamentos.
 */
function formatEstimativaData(estimativa) {
  if (!estimativa) return null;

  const parsedItens = safeParseItens(estimativa.itens);
  const equipamentoInfo = parsedItens?.equipamento_info || null;
  const servicos = Array.isArray(parsedItens?.servicos)
    ? parsedItens.servicos.map((servico) => {
      const quantidade = Number(servico?.quantidade || 1);
      const valorUnitario = Number(servico?.valorUnitario ?? servico?.valor ?? 0);
      const valorTotal = Number(servico?.valorTotal ?? (valorUnitario * quantidade));

      return {
        ...servico,
        nome: servico?.nome || servico?.servico || '-',
        quantidade,
        valorUnitario,
        valorTotal,
      };
    })
    : [];
  const localizacao = parsedItens?.localizacao || null;
  const equipamentos = Array.isArray(parsedItens?.equipamentos) ? parsedItens.equipamentos : [];
  const itensNormalizados = parsedItens
    ? {
      ...parsedItens,
      equipamento_info: equipamentoInfo
        ? {
          ...equipamentoInfo,
          marca: equipamentoInfo.marca || '-',
          modelo: equipamentoInfo.modelo || '-',
          problema: equipamentoInfo.problema || '-',
          quantidade: equipamentoInfo.quantidade || '-',
        }
        : null,
      servicos,
      localizacao: localizacao
        ? {
          ...localizacao,
          tipo: localizacao.tipo || '-',
          endereco: localizacao.endereco || '-',
        }
        : null,
      equipamentos,
    }
    : null;

  return {
    id: estimativa.id,
    tipo: estimativa.tipo,
    status: estimativa.status,
    itens: itensNormalizados,
    texto_resposta: estimativa.textoResposta || estimativa.texto_resposta,
    valor_resposta: estimativa.valorResposta || estimativa.valor_resposta,
    motivo_recusa: estimativa.motivoRecusa || estimativa.motivo_recusa,
    user_id: estimativa.userId || estimativa.user_id,
    orcamento_id: estimativa.orcamentoId || estimativa.orcamento_id,
    created_at: estimativa.createdAt || estimativa.created_at,
    updated_at: estimativa.updatedAt || estimativa.updated_at,
    companyId: estimativa.companyId,
    // Derive endereco from parsed itens.localizacao or itens.maintenanceLocalization for the EstimativaSection
    endereco: localizacao
      ? `${localizacao.tipo || ''} - ${localizacao.endereco || ''}`
      : parsedItens?.maintenanceLocalization
        ? parsedItens.maintenanceLocalization
        : estimativa.endereco || '-',
  };
}

/**
 * Format orcamento (budget) data from the API response.
 */
function formatOrcamentoData(orcamento) {
  if (!orcamento) return null;

  return {
    id: orcamento.id,
    status: orcamento.status,
    valor: orcamento.valor || orcamento.value,
    observacoes: orcamento.observacoes || orcamento.observations,
    itens: Array.isArray(orcamento.itens) ? orcamento.itens.map(item => ({
      equip_id: item.equip_id || item.equipment_id,
      equipamento_nome: item.equipamento_nome || item.equipment_name,
      equipamento_marca: item.equipamento_marca || item.equipment_brand,
      equipamento_modelo: item.equipamento_modelo || item.equipment_model,
      equipamento_serie: item.equipamento_serie || item.equipment_serial,
      tipo_item: item.tipo_item || item.item_type,
      codigo_interno: item.codigo_interno || item.internal_code,
      nome: item.nome || item.name,
      qtd: item.qtd || item.quantity,
      valor: item.valor || item.value,
    })) : [],
    created_at: orcamento.createdAt || orcamento.created_at,
    updated_at: orcamento.updatedAt || orcamento.updated_at,
  };
}

/**
 * Main mapper: receives the raw API response from client/quote-details/{id}
 * and produces the normalized object that the detail pages consume.
 *
 * @param {Object} data - Raw response body from GET client/quote-details/{id}
 * @returns {{ cliente: Object|null, estimativa: Object|null, orcamento: Object|null, rotasimplesempresa: string|null }}
 */
export function parseQuoteDetailsResponse(data) {
  if (!data) {
    return { cliente: null, estimativa: null, orcamento: null, rotasimplesempresa: null };
  }

  const baseClient = formatClientData(data.cliente);
  const estimativa = formatEstimativaData(data.estimativa);
  const orcamento = formatOrcamentoData(data.orcamento);
  const serviceFiles = Array.isArray(data.serviceFiles) ? data.serviceFiles : [];

  let cliente = baseClient ? { ...baseClient } : {};
  const itensClient = estimativa?.itens?.cliente;

  if (itensClient) {
    cliente.empresa = itensClient.empresa || itensClient.nome || baseClient?.empresa || '-';
    cliente.nome = itensClient.responsavel || itensClient.cliente_user_name || baseClient?.nome || itensClient.nome || '-';
    if (itensClient.telefone) cliente.telefone = itensClient.telefone;
    if (itensClient.email) cliente.email = itensClient.email;
    if (itensClient.cnpj) cliente.cnpj = itensClient.cnpj;
  }

  if (!baseClient && !itensClient) {
    cliente = null;
  }

  return {
    cliente,
    estimativa,
    orcamento,
    serviceFiles,
    rotasimplesempresa: cliente?.empresa || null,
  };
}
